import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { AuthService } from '../../services/auth.service';
import { WebSocketService } from '../../services/websocket.service';
import { ChatService } from '../../services/chat.service';
import { ProximityService } from '../../services/proximity.service';
import { QrService } from '../../services/qr-service.service';
import { LocationService } from '../../services/location.service';
import { OrderNotificationService } from '../../services/order-notification.service';
import { PushNotificationService } from '../../services/push-notification.service';

interface Category {
  id: string;
  name: string;
  icon: string;
  gradient: string;
  route?: string;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ZXingScannerModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, OnDestroy {
  currentUser: any = null;
  isWebSocketConnected = false;
  nearbyUsersCount = 0;
  unreadMessagesCount = 0;
  proximityEnabled = false;
  private subscriptions = new Subscription();

  quickActions: any[] = [];
  recentActivity: any[] = [];

  personalStats = {
    ordersCount: 0,
    placesVisited: 0,
    reviewsWritten: 0,
    pointsEarned: 0
  };

  categories: Category[] = [
    { id: 'search', name: 'Buscar', icon: '🔍', gradient: 'linear-gradient(135deg, #1A1A1A, #121212)', route: '/buscar' },
    { id: 'nearby', name: 'Cerca', icon: '📍', gradient: 'linear-gradient(135deg, #222, #1A1A1A)', route: '/nearby-restaurants' },
    { id: 'history', name: 'Historial', icon: '📜', gradient: 'linear-gradient(135deg, #2A2A2A, #222)', route: '/order-history' },
  ];

  isDarkMode = false;

  // QR Scanner modal state
  showQrModal = false;
  qrHasPermission = false;
  qrHasDevices = false;
  qrAvailableDevices: MediaDeviceInfo[] = [];
  qrCurrentDevice: MediaDeviceInfo | undefined;
  qrProcessing = false;
  qrError: string | null = null;
  qrAllowedFormats = [BarcodeFormat.QR_CODE, BarcodeFormat.EAN_13, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX];

  constructor(
    private authService: AuthService,
    private wsService: WebSocketService,
    private chatService: ChatService,
    private proximityService: ProximityService,
    private qrService: QrService,
    private locationService: LocationService,
    private orderNotificationService: OrderNotificationService,
    private pushNotificationService: PushNotificationService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.isDarkMode = document.body.classList.contains('dark-theme');
    this.setupWebSocketConnection();
    this.setupSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupWebSocketConnection(): void {
    const token = this.authService.getToken();
    if (token && this.currentUser) {
      this.wsService.connect(token);
      this.chatService.setCurrentUser(this.currentUser.id);
      // Start listening for order status notifications globally
      this.orderNotificationService.init();

      // Subscribe to Web Push for background notifications
      if (Notification.permission === 'granted') {
        this.pushNotificationService.subscribeToPush();
        this.pushNotificationService.setupClickHandler();
      }
    }
  }

  private setupSubscriptions(): void {
    this.subscriptions.add(
      this.wsService.connectionStatus$.subscribe(connected => {
        this.isWebSocketConnected = connected;
      })
    );

    this.subscriptions.add(
      this.proximityService.nearbyUsers$.subscribe(users => {
        this.nearbyUsersCount = users.length;
      })
    );

    this.subscriptions.add(
      this.proximityService.settings$.subscribe(settings => {
        this.proximityEnabled = settings.enabled;
      })
    );

    this.subscriptions.add(
      this.chatService.activeRooms$.subscribe(rooms => {
        this.unreadMessagesCount = rooms.reduce((total, room) => total + room.unreadCount, 0);
      })
    );
  }

  onActivityClick(activity: any): void {
    console.log('Activity:', activity);
  }

  onToggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    document.body.classList.toggle('dark-theme', this.isDarkMode);
    localStorage.setItem('barclik-theme', this.isDarkMode ? 'dark' : 'light');
  }

  onCategoryClick(category: Category): void {
    if (category.route) {
      this.router.navigate([category.route]);
    }
  }

  // ---- QR Scanner Modal ----
  openQrModal(): void {
    this.showQrModal = true;
    this.qrError = null;
    this.qrProcessing = false;
    this.requestCameraPermission();
  }

  closeQrModal(): void {
    this.showQrModal = false;
    this.qrHasPermission = false;
    this.qrHasDevices = false;
    this.qrCurrentDevice = undefined;
    this.qrError = null;
    this.qrProcessing = false;
  }

  async requestCameraPermission(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.qrHasPermission = true;
      stream.getTracks().forEach(track => track.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.qrAvailableDevices = devices.filter(d => d.kind === 'videoinput');
      this.qrHasDevices = this.qrAvailableDevices.length > 0;
      if (this.qrHasDevices) {
        this.qrCurrentDevice = this.qrAvailableDevices.find(d =>
          d.label.toLowerCase().includes('back') || d.label.toLowerCase().includes('rear')
        ) || this.qrAvailableDevices[0];
      }
      this.cdr.detectChanges();
    } catch {
      this.qrHasPermission = false;
      this.qrError = 'No se pudo acceder a la camara. Permite el acceso.';
      this.cdr.detectChanges();
    }
  }

  onQrCamerasFound(devices: MediaDeviceInfo[]): void {
    this.qrAvailableDevices = devices;
    this.qrHasDevices = devices?.length > 0;
  }

  onQrPermission(has: boolean): void {
    this.qrHasPermission = has;
  }

  onQrScanSuccess(result: string): void {
    if (this.qrProcessing) return;
    this.qrProcessing = true;
    this.qrError = null;
    this.cdr.detectChanges();

    if (this.qrService.isValidQRFormat(result)) {
      this.processBarClikQR(result);
    } else {
      this.qrError = 'Codigo QR no reconocido como BarClik.';
      this.qrProcessing = false;
      this.cdr.detectChanges();
    }
  }

  onQrScanError(error: any): void {
    console.error('QR scan error:', error);
  }

  private async processBarClikQR(qrData: string): Promise<void> {
    try {
      const location = await this.locationService.getCurrentPosition().toPromise();
      if (!location) {
        throw new Error('No se pudo obtener la ubicacion. Activa el GPS.');
      }

      const scanResult = await this.qrService.scanQRCode(qrData).toPromise();

      if (scanResult?.success) {
        this.closeQrModal();
        this.router.navigate(['/restaurant-menu'], {
          state: {
            restaurant: scanResult.restaurant,
            menu: scanResult.menu,
            source: 'qr-scan'
          }
        });
      } else {
        throw new Error(scanResult?.error || 'Error procesando el codigo QR');
      }
    } catch (error: any) {
      this.qrError = error.message || 'Error desconocido al procesar el QR';
      this.qrProcessing = false;
      this.cdr.detectChanges();
    }
  }
}
